import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { channelsHandler, handleChannelRemove, handleGroupDevicesFilter, handleReallocateChannels } from './helpers';

const mapGroupListNode = (group: any): any => ({
    id: group?.id,
    name: group?.name,
    icon: group?.icon,
    type: group?.type,
    deviceCount: group?.deviceCount,
    indicator: group?.indicator ?? '',
    groupPath: group?.groupPath,
    alarmCount: group?.alarmCount ?? 0,
    offlineCount: group?.offlineCount ?? 0,
    warningCount: group?.warningCount ?? 0,
    informationalCount: group?.informationalCount ?? 0,
    statusCount: {
        alarms: group?.alarmCount ?? 0,
        offline: group?.offlineCount ?? 0,
        warnings: group?.warningCount ?? 0,
        information: group?.informationalCount ?? 0,
        snooze: group?.snoozeCount ?? 0,
        upcomming: group?.upcommingCount ?? 0,
    },
});

const groupListInitialState = {
    secondary: [],
    filters: {},
    selectedNodes: undefined,
    maxCount: 6,
    isTotalCountVisible: false,
    viewAllButton: false,
    sortPayload: {
        key: 'name',
        sortType: 'DESC',
    },
    countMode: 'custom',
};
const groupListWidgetSlice = createSlice({
    name: 'group_list',
    initialState: groupListInitialState,
    reducers: {
        resetGroupListConfiguration: () => {
            return groupListInitialState;
        },
        setPredefinedGroupListConfiguration: (state: RootState, action: PayloadAction<any>) => {
            Object.keys(action.payload)?.forEach((key: string) => {
                if (key !== 'filters') {
                    state[key] = action.payload[key];
                }
            });
        },
        setGroupData: (state: RootState, action: PayloadAction<any>) => {
            state.secondary = action.payload?.map((group: any) => ({
                id: group?.id,
                name: group?.name,
                icon: group?.icon,
                type: group?.type,
                deviceCount: group?.deviceCount,
                indicator: group?.indicator ?? '',
                groupPath: group?.groupPath,
                statusCount: {
                    alarms: group?.alarmCount ?? 0,
                    offline: group?.offlineCount ?? 0,
                    warnings: group?.warningCount ?? 0,
                    information: group?.informationalCount ?? 0,
                    snooze: group?.snoozeCount ?? 0,
                    upcomming: group?.upcommingCount ?? 0,
                },
            }));
            const responseGroups = action.payload?.map((group: any) => mapGroupListNode(group)) ?? [];
            const missingSelectedNodes =
                state.selectedNodes
                    ?.filter?.((node: any) => !responseGroups.find((group: any) => group.id === node?.id))
                    ?.map?.((node: any) => mapGroupListNode(node)) ?? [];

            state.secondary = [...responseGroups, ...missingSelectedNodes];
        },
        setGroup: (state: RootState, action: PayloadAction<any>) => {
            channelsHandler(state, action);
        },
        setGroupListFilters: (state: RootState, action: PayloadAction<any>) => {
            state['filters'] = action.payload;
        },
        setGroupListSelectedNodes: (state: RootState, action: PayloadAction<any>) => {
            handleGroupDevicesFilter(state, action);
        },
        setGroupListSortPayloadOrder: (state: RootState, action: PayloadAction<any>) => {
            state['sortPayload']['sortType'] = action.payload;
        },
        setGroupListSortPayloadKey: (state: RootState, action: PayloadAction<any>) => {
            state['sortPayload']['key'] = action.payload;
        },
        setGroupHierarchyFilter: (state: RootState, action: PayloadAction<string>) => {
            state['filters'] = action.payload;
        },
        reallocateGroupList: (state: RootState, action: PayloadAction<any>) => {
            handleReallocateChannels(state, action);
        },
        removeGroup: (state: RootState, action: PayloadAction<any>) => {
            handleChannelRemove(state, action, 'id');
        },
        setGroupListPreferences: (state: any, action: PayloadAction<any>) => {
            state[action.payload.key] = action.payload.value;
        },
    },
    extraReducers: (builder) => {
        builder.addCase('widgetConfiguration/toggleDeviceLinkMode', (state: RootState) => {
            state.secondary = [];
            state.selectedNodes = [];
            state.filters = {};
        });

        builder.addCase('widgetConfiguration/setWidgetDevice', (state: RootState, action: any) => {
            if (!action.payload.reset) {
                state.secondary = [];
            }
            state['deviceId'] = action.payload?.data?.id;
        });
    },
});

export const {
    reallocateGroupList,
    removeGroup,
    setGroupListPreferences,
    setGroupHierarchyFilter,
    setGroupListFilters,
    setGroupListSelectedNodes,
    setPredefinedGroupListConfiguration,
    setGroupListSortPayloadKey,
    setGroupListSortPayloadOrder,
    resetGroupListConfiguration,
    setGroupData,
} = groupListWidgetSlice.actions;

export default groupListWidgetSlice;
